import React, { useState, useCallback, useRef } from 'react';
import { Play, RotateCcw, CheckCircle, XCircle, Clock, Trash2, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// 直接导入 Monaco Editor，避免生成 chunk 文件
import Editor from '@monaco-editor/react';

interface PythonCodeEditorProps {
  initialCode?: string;
  starterCode?: string;
  onRun?: (code: string) => void;
  onSubmit?: (code: string) => void;
  isRunning?: boolean;
  isSubmitting?: boolean;
  output?: string;
  error?: string;
  executionTime?: number;
  turtleSvg?: string | null;
  readOnly?: boolean;
  height?: string;
  submitLabel?: string;
}

export default function PythonCodeEditor({
  initialCode = '',
  starterCode = '',
  onRun,
  onSubmit,
  isRunning = false,
  isSubmitting = false,
  output = '',
  error = '',
  executionTime = 0,
  turtleSvg = null,
  readOnly = false,
  height = '400px',
  submitLabel = '保存并提交',
}: PythonCodeEditorProps) {
  const [code, setCode] = useState(initialCode || starterCode);
  const editorRef = useRef<any>(null);
  const [confirmAction, setConfirmAction] = useState<'reset' | 'clear' | null>(null);

  const handleReset = useCallback(() => {
    setCode(starterCode);
    if (editorRef.current) {
      editorRef.current.setValue(starterCode);
    }
    setConfirmAction(null);
  }, [starterCode]);

  const handleClear = useCallback(() => {
    setCode('');
    if (editorRef.current) {
      editorRef.current.setValue('');
    }
    setConfirmAction(null);
  }, []);

  const requestReset = useCallback(() => {
    setConfirmAction('reset');
  }, []);

  const requestClear = useCallback(() => {
    setConfirmAction('clear');
  }, []);

  const cancelConfirm = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleRun = useCallback(() => {
    onRun?.(code);
  }, [code, onRun]);

  const handleSubmit = useCallback(() => {
    onSubmit?.(code);
  }, [code, onSubmit]);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 border-b border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">Py</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Python 代码编辑器</h3>
              <p className="text-xs text-gray-500">支持 Python 3.x / Turtle 绘图</p>
            </div>
          </div>
          <div className="flex items-center gap-2 relative">
            {onRun && (
              <button
                onClick={handleRun}
                disabled={isRunning || isSubmitting || readOnly}
                className="px-3 py-1.5 text-sm text-green-600 hover:text-green-700 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <Play className="w-4 h-4" />
                {isRunning ? '运行中...' : '运行测试'}
              </button>
            )}
            <button
              onClick={requestReset}
              disabled={readOnly}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <RotateCcw className="w-4 h-4" />
              重置代码
            </button>
            <button
              onClick={requestClear}
              disabled={readOnly}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              清空
            </button>
            <AnimatePresence>
              {confirmAction && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-[220px]"
                >
                  <p className="text-sm text-gray-700 mb-3">
                    {confirmAction === 'reset'
                      ? '确定要将代码恢复到初始状态吗？'
                      : '确定要清空所有代码吗？'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={confirmAction === 'reset' ? handleReset : handleClear}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                        confirmAction === 'clear'
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      {confirmAction === 'reset' ? <RotateCcw className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                      确定
                    </button>
                    <button
                      onClick={cancelConfirm}
                      className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      取消
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Editor - 使用 Monaco Editor */}
      <div className="border-b border-gray-200">
        <Editor
          height={height}
          language="python"
          theme="vs-dark"
          value={code}
          onChange={(value) => setCode(value || '')}
          onMount={handleEditorDidMount}
          options={{
            fontSize: 14,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 4,
            insertSpaces: true,
            readOnly: readOnly,
          }}
        />
      </div>

      {/* Output Area */}
      {(output || error || turtleSvg || executionTime > 0) && (
        <div className="bg-gray-50 border-t border-gray-200">
          <div className="p-4 space-y-4">
            {/* Turtle SVG 输出 */}
            {turtleSvg && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Turtle 绘图结果</span>
                </div>
                <div className="flex justify-center" dangerouslySetInnerHTML={{ __html: turtleSvg }} />
              </div>
            )}

            {/* 执行错误 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-red-700 mb-2">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">执行错误</span>
                </div>
                <pre className="text-sm text-red-600 whitespace-pre-wrap font-mono overflow-auto max-h-40">
                  {error}
                </pre>
              </div>
            )}

            {/* 执行输出 */}
            {output && !error && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">执行成功</span>
                </div>
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono overflow-auto max-h-40">
                  {output}
                </pre>
              </div>
            )}

            {/* 执行时间 */}
            {executionTime > 0 && (
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>执行时间: {executionTime}ms</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      {onSubmit && (
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
          <button
            onClick={handleSubmit}
            disabled={isRunning || isSubmitting || readOnly}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? '保存中...' : submitLabel}
          </button>
        </div>
      )}
    </div>
  );
}
